import { createSlice } from "@reduxjs/toolkit";
import { OpenCall, Meta } from "src/store/types";
import {
  doFetchOpenCallList,
  doCreateOpenCall,
  doGetOpenCallDetails,
  doUpdateOpenCall,
  doBlockUnblockOpenCall,
  doDeleteOpenCall,
  doFetchAuditionsByOpenCall,
  doBlockUnblockAudition,
  doFetchReportedAuditions,
  doDeleteAudition,
  doFetchAuditionReports,
} from "./opencall.action";

interface OpenCallState {
  openCalls: OpenCall[];
  selectedOpenCall: OpenCall | null;
  meta: Meta | null;
  loading: boolean;
  detailsLoading: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  blockUnblockLoading: boolean;
  auditions: any[];
  auditionMeta: Meta | null;
  reportedAuditions: any[];
  reportedAuditionMeta: Meta | null;
  auditionLoading: boolean;
  reportedAuditionLoading: boolean;
  deleteAuditionLoading: boolean;
  error: string | null;
  searchQuery: string;
  auditionSearchQuery: string;
  reportedAuditionSearchQuery: string;
  auditionReportDetails: [];
  auditionReportDetailsLoading: boolean;
}

const initialState: OpenCallState = {
  openCalls: [] as OpenCall[],
  selectedOpenCall: null,
  meta: null,
  loading: false,
  detailsLoading: false,
  createLoading: false,
  updateLoading: false,
  blockUnblockLoading: false,
  auditions: [],
  auditionMeta: null,
  reportedAuditions: [],
  reportedAuditionMeta: null,
  auditionLoading: false,
  reportedAuditionLoading: false,
  deleteAuditionLoading: false,
  error: null,
  searchQuery: "",
  auditionSearchQuery: "",
  reportedAuditionSearchQuery: "",
  auditionReportDetails: [],
  auditionReportDetailsLoading: false,
};

export const openCallSlice = createSlice({
  name: "openCall",
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = "";
    },
    setAuditionSearchQuery: (state, action) => {
      state.auditionSearchQuery = action.payload;
    },
    clearAuditionSearch: (state) => {
      state.auditionSearchQuery = "";
    },
    setReportedAuditionSearchQuery: (state, action) => {
      state.reportedAuditionSearchQuery = action.payload;
    },
    clearReportedAuditionSearch: (state) => {
      state.reportedAuditionSearchQuery = "";
    },
    clearSelectedOpenCall: (state) => {
      state.selectedOpenCall = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetch open call list
    builder
      .addCase(doFetchOpenCallList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doFetchOpenCallList.fulfilled, (state, action) => {
        state.loading = false;
        state.openCalls = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(doFetchOpenCallList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // create open call
    builder
      .addCase(doCreateOpenCall.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(doCreateOpenCall.fulfilled, (state, action) => {
        state.createLoading = false;
        if (action.payload.data) {
          state.openCalls.unshift(action.payload.data);
        }
      })
      .addCase(doCreateOpenCall.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      });

    // get open call details
    builder
      .addCase(doGetOpenCallDetails.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })
      .addCase(doGetOpenCallDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedOpenCall = action.payload.data;
      })
      .addCase(doGetOpenCallDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload as string;
      });

    // update open call
    builder
      .addCase(doUpdateOpenCall.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(doUpdateOpenCall.fulfilled, (state, action) => {
        state.updateLoading = false;
        if (action.payload.data) {
          const updatedOpenCall = action.payload.data;
          // Update in the list
          state.openCalls = state.openCalls.map((openCall) =>
            openCall.ocid === updatedOpenCall.ocid ? updatedOpenCall : openCall
          );
          // Update selected open call if it's the same one
          if (state.selectedOpenCall?.ocid === updatedOpenCall.ocid) {
            state.selectedOpenCall = updatedOpenCall;
          }
        }
      })
      .addCase(doUpdateOpenCall.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload as string;
      });

    // block/unblock open call
    builder
      .addCase(doBlockUnblockOpenCall.pending, (state) => {
        state.blockUnblockLoading = true;
        state.error = null;
      })
      .addCase(doBlockUnblockOpenCall.fulfilled, (state, action) => {
        state.blockUnblockLoading = false;
        if (action.payload.data) {
          const updatedOpenCall = action.payload.data;
          // Update in the list
          state.openCalls = state.openCalls.map((openCall) =>
            openCall.ocid === updatedOpenCall.ocid ? updatedOpenCall : openCall
          );
          // Update selected open call if it's the same one
          if (state.selectedOpenCall?.ocid === updatedOpenCall.ocid) {
            state.selectedOpenCall = updatedOpenCall;
          }
        }
      })
      .addCase(doBlockUnblockOpenCall.rejected, (state, action) => {
        state.blockUnblockLoading = false;
        state.error = action.payload as string;
      });

    // delete open call
    builder
      .addCase(doDeleteOpenCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doDeleteOpenCall.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the post from both arrays
        const deletedOpenCallId = action.payload.pid;
        state.openCalls = state.openCalls.filter(
          (openCall) => openCall.ocid !== deletedOpenCallId
        );
      })
      .addCase(doDeleteOpenCall.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Auditions by OCID
    builder
      .addCase(doFetchAuditionsByOpenCall.pending, (state) => {
        state.auditionLoading = true;
        state.error = null;
      })
      .addCase(doFetchAuditionsByOpenCall.fulfilled, (state, action) => {
        state.auditionLoading = false;
        state.auditions = action.payload.data;
        state.auditionMeta = action.payload.meta;
      })
      .addCase(doFetchAuditionsByOpenCall.rejected, (state, action) => {
        state.auditionLoading = false;
        state.error = action.payload as string;
      });

    // Block/unblock audition
    builder.addCase(doBlockUnblockAudition.fulfilled, (state, action) => {
      const updatedAudition = action.payload.data;
      state.auditions = state.auditions.map((item) =>
        item.pid === updatedAudition.pid ? updatedAudition : item
      );
    });

    // Fetch reported auditions
    builder
      .addCase(doFetchReportedAuditions.pending, (state) => {
        state.reportedAuditionLoading = true;
        state.error = null;
      })
      .addCase(doFetchReportedAuditions.fulfilled, (state, action) => {
        state.reportedAuditionLoading = false;
        state.reportedAuditions = action.payload.data;
        state.reportedAuditionMeta = action.payload.meta;
      })
      .addCase(doFetchReportedAuditions.rejected, (state, action) => {
        state.reportedAuditionLoading = false;
        state.error = action.payload as string;
      });

    // Fetch audition reportes
    builder
      .addCase(doFetchAuditionReports.pending, (state) => {
        state.auditionReportDetailsLoading = true;
        state.error = null;
      })
      .addCase(doFetchAuditionReports.fulfilled, (state, action) => {
        state.auditionReportDetailsLoading = false;
        state.auditionReportDetails = action.payload.reports || [];
      })
      .addCase(doFetchAuditionReports.rejected, (state, action) => {
        state.auditionReportDetailsLoading = false;
        state.error = action.payload as string;
      });

    // Delete audition
    builder
      .addCase(doDeleteAudition.pending, (state) => {
        state.deleteAuditionLoading = true;
        state.error = null;
      })
      .addCase(doDeleteAudition.fulfilled, (state, action) => {
        state.deleteAuditionLoading = false;
        const deletedPid = action.payload.pid;
        state.auditions = state.auditions.filter(
          (item) => item.pid !== deletedPid
        );
        state.reportedAuditions = state.reportedAuditions.filter(
          (item) => item.pid !== deletedPid
        );
      })
      .addCase(doDeleteAudition.rejected, (state, action) => {
        state.deleteAuditionLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  setSearchQuery,
  clearSearch,
  setAuditionSearchQuery,
  clearAuditionSearch,
  setReportedAuditionSearchQuery,
  clearReportedAuditionSearch,
  clearSelectedOpenCall,
  clearError,
} = openCallSlice.actions;

export default openCallSlice.reducer;
